import { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

const galleryImages = [
  {
    src: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    alt: 'Hair styling',
    category: 'Styling',
  },
  {
    src: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    alt: 'Salon interior',
    category: 'Salong',
  },
  {
    src: 'https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    alt: 'Hair coloring',
    category: 'Färg',
  },
  {
    src: 'https://images.unsplash.com/photo-1620331311523-7cb1d3738d23?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    alt: 'Blow dry',
    category: 'Styling',
  },
  {
    src: 'https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    alt: 'Hair treatment',
    category: 'Behandling',
  },
  {
    src: 'https://images.unsplash.com/photo-1580619073298-8bc2c748df6a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    alt: 'Styling tools',
    category: 'Salong',
  },
]

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null)

  const openLightbox = (index: number) => {
    setSelectedImage(index)
  }

  const closeLightbox = () => {
    setSelectedImage(null)
  }

  const goToPrevious = () => {
    setSelectedImage((prev) => (prev === null || prev === 0 ? galleryImages.length - 1 : prev - 1))
  }

  const goToNext = () => {
    setSelectedImage((prev) => (prev === null || prev === galleryImages.length - 1 ? 0 : (prev || 0) + 1))
  }

  return (
    <section id="galleri" className="relative py-24 lg:py-32 bg-dark-900">
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-gold-400 font-semibold text-sm tracking-wider uppercase mb-4">
            Portfolio
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Vårt <span className="text-gradient">galleri</span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Inspireras av några av våra arbeten – från eleganta uppsättningar till moderna färgningar.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className="group relative aspect-square overflow-hidden rounded-xl cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 via-dark-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block px-3 py-1 bg-gold-500/20 text-gold-300 text-sm font-medium rounded-full">
                    {image.category}
                  </span>
                  <h4 className="mt-2 text-white font-display text-lg">{image.alt}</h4>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div className="fixed inset-0 z-50 bg-dark-900/95 backdrop-blur-lg flex items-center justify-center p-4">
          <button
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            onClick={closeLightbox}
          >
            <X className="w-6 h-6" />
          </button>

          <button
            className="absolute left-4 lg:left-8 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            onClick={goToPrevious}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            className="absolute right-4 lg:right-8 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            onClick={goToNext}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="max-w-4xl max-h-[80vh]">
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="w-full h-full object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <span className="text-gold-400 font-medium">{galleryImages[selectedImage].category}</span>
              <h4 className="text-white font-display text-xl mt-1">{galleryImages[selectedImage].alt}</h4>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
